<template>
  <div>
    <div class="wz-page-header">
      <h1 class="wz-page-title">Entidades pensionarias</h1>
    </div>

    <wz-table-container>

      <wz-table-toolbar>
        <template #left>
          <wz-filter-menu
            :options="filterOptions"
            :selected-option-filter="filters"
            @setFilters="setFilters"
          />
        </template>

        <template #right>
          <wz-tooltip text="Recargar">
            <wz-button variant="outline" @click="loadData()">
              <i class="ri-loop-right-line"></i>
            </wz-button>
          </wz-tooltip>
          <wz-search-bar
            placeholder="Buscar por nombre o código"
            @input="search = $event"
            @search="search = $event"
          />
          <wz-button @click="$router.push({ name: 'pension-entity-create' })">Agregar</wz-button>
        </template>

        <template #filters v-if="showFiltersList">
          <wz-badge
            v-for="(val, name) in filterList"
            :key="name"
            variant="primary"
            pill
            dismissible
            @dismiss="deleteFilter(name)"
          >
            {{ getFilterName(name, val) }}
          </wz-badge>
        </template>
      </wz-table-toolbar>

      <wz-table>
        <thead>
          <tr>
            <th class="wz-th-sortable" :class="{ 'is-sorted': sortKey === 'name' }" @click="toggleSort('name')">
              Nombre <i :class="['wz-th-sort-icon', sortIcon('name')]"></i>
            </th>
            <th class="wz-th-sortable" :class="{ 'is-sorted': sortKey === 'code' }" @click="toggleSort('code')">
              Código <i :class="['wz-th-sort-icon', sortIcon('code')]"></i>
            </th>
            <th class="wz-th-sortable" :class="{ 'is-sorted': sortKey === 'type' }" @click="toggleSort('type')">
              Tipo <i :class="['wz-th-sort-icon', sortIcon('type')]"></i>
            </th>
            <th class="wz-th-sortable" :class="{ 'is-sorted': sortKey === 'mandatoryContributionRate' }" @click="toggleSort('mandatoryContributionRate')">
              Aporte oblig. <i :class="['wz-th-sort-icon', sortIcon('mandatoryContributionRate')]"></i>
            </th>
            <th class="wz-th-sortable" :class="{ 'is-sorted': sortKey === 'insurancePremiumRate' }" @click="toggleSort('insurancePremiumRate')">
              Prima seguro <i :class="['wz-th-sort-icon', sortIcon('insurancePremiumRate')]"></i>
            </th>
            <th class="wz-th-sortable" :class="{ 'is-sorted': sortKey === 'flowCommissionRate' }" @click="toggleSort('flowCommissionRate')">
              Com. flujo <i :class="['wz-th-sort-icon', sortIcon('flowCommissionRate')]"></i>
            </th>
            <th class="wz-th-sortable" :class="{ 'is-sorted': sortKey === 'status' }" @click="toggleSort('status')">
              Estado <i :class="['wz-th-sort-icon', sortIcon('status')]"></i>
            </th>
            <th>Opciones</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="filteredList.length === 0">
            <td colspan="9" class="text-center text-muted py-4">No se encontraron resultados.</td>
          </tr>
          <tr v-for="o in sortedList(filteredList)" :key="o.id">
            <td>{{ o.name }}</td>
            <td>{{ o.code }}</td>
            <td>
              <wz-badge :variant="o.type === 'AFP' ? 'info-solid' : 'dark'" pill>
                {{ o.type }}
              </wz-badge>
            </td>
            <td style="text-align: right;">{{ formatRate(o.mandatoryContributionRate) }}</td>
            <td style="text-align: right;">{{ o.type === 'AFP' ? formatRate(o.insurancePremiumRate) : '-' }}</td>
            <td style="text-align: right;">{{ o.type === 'AFP' ? formatRate(o.flowCommissionRate) : '-' }}</td>
            <td>
              <wz-badge :variant="statusVariant(o.status)" pill>
                {{ getStatus(o.status).label }}
              </wz-badge>
            </td>
            <td style="text-align: center;">
              <wz-dropdown
                :items="[
                  { key: 'edit',   label: 'Editar' },
                  { key: 'status', label: getStatus(o.status).action },
                ]"
                placement="left"
                @click="handleAction(o, $event)"
              />
            </td>
          </tr>
        </tbody>
      </wz-table>

      <wz-table-footer>
        <span>{{ filteredList.length }} registro(s)</span>
      </wz-table-footer>

    </wz-table-container>

    <wz-confirm-modal
      v-model="confirmModal.show"
      :title="confirmModal.title"
      :description="confirmModal.description"
      :accept-text="confirmModal.acceptText"
      :cancel-text="confirmModal.cancelText"
      @confirm="onModalConfirm"
      @cancel="closeConfirmModal"
    />
  </div>
</template>

<script>
import { Alert } from "@wizepe/wizeui"
import { Util } from "@wizepe/wizeui"
import { FilterMixin } from "@wizepe/wizeui"
import { SortMixin, ConfirmModalMixin } from '@wizepe/wizeui'
import { Constants as LocalConstants } from '@/core/utils'
import { PensionEntityService } from '@/core/services'

export default {
  mixins: [SortMixin, FilterMixin, ConfirmModalMixin],
  data() {
    return {
      list: [],
      search: '',
      filterOptions: [
        {
          label: 'Estado',
          key: 'status',
          type: 'SINGLE',
          options: [LocalConstants.PensionEntityStatus.ACTIVE, LocalConstants.PensionEntityStatus.INACTIVE],
        },
      ],
    }
  },
  computed: {
    filteredList() {
      let result = this.list
      if (this.filters.status != null) {
        result = result.filter(o => parseInt(o.status) === this.filters.status)
      }
      if (this.search) {
        const q = this.search.toLowerCase()
        result = result.filter(o =>
          (o.name || '').toLowerCase().includes(q) ||
          (o.code || '').toLowerCase().includes(q)
        )
      }
      return result
    },
  },
  methods: {
    statusVariant(status) {
      return this.getStatus(status)?.color || 'dark'
    },
    handleAction(o, item) {
      if (item.key === 'edit')   this.$router.push({ name: 'pension-entity-edit', params: { id: o.id } })
      if (item.key === 'status') this.openChangeStatus(o)
    },
    async loadData() {
      try {
        await this.$store.dispatch('app/loading', true)
        const resp = await PensionEntityService.list()
        this.list = (resp.payload || []).map(item => ({ ...item }))
      } catch (e) {
        console.error(e)
        Alert.error(e.errors ? e.errors.message : 'Error al cargar las entidades')
      } finally {
        await this.$store.dispatch('app/loading', false)
      }
    },
    getStatus(key) {
      return Util.searchValue(parseInt(key), LocalConstants.PensionEntityStatus)
    },
    formatRate(val) {
      if (val === null || val === undefined) return '-'
      return (val * 100).toFixed(2) + '%'
    },
    openChangeStatus(o) {
      this.selectedOption = o
      this.openConfirmModal({
        title:       'Cambiar estado',
        description: '¿Está seguro que desea cambiar el estado de esta entidad?',
        acceptText:  'Aceptar',
        action:      'update',
      })
    },
    onModalConfirm() {
      this.closeConfirmModal()
      if (this.confirmModal.action === 'update') this.changeStatus()
    },
    async changeStatus() {
      try {
        await this.$store.dispatch('app/loading', true)
        const current   = parseInt(this.selectedOption.status)
        const newStatus = current === LocalConstants.PensionEntityStatus.ACTIVE.key
          ? LocalConstants.PensionEntityStatus.INACTIVE.key
          : LocalConstants.PensionEntityStatus.ACTIVE.key
        await PensionEntityService.changeStatus(this.selectedOption.id, newStatus)
        await this.loadData()
        Alert.success('Estado actualizado correctamente')
      } catch (ex) {
        console.error(ex)
        Alert.error(ex.errors ? ex.errors.message : 'Error al cambiar el estado')
      } finally {
        await this.$store.dispatch('app/loading', false)
      }
    },
  },
  async created() {
    await this.loadData()
  },
}
</script>

<style lang="stylus">
</style>
